import {
  useEffect, useState, useContext, useCallback,
} from 'react';
import { useNavigate, Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { AuthContext } from '../context/AuthContext';
import { getUserPuzzles, deletePuzzle } from '../services/puzzleApi';
import { formatTimeHuman } from '../utils/formatters';
import LoadingSpinner from '../components/ui/LoadingSpinner';

function PuzzleCard({ puzzle, onDelete }) {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Are you sure you want to delete this puzzle?')) return;

    setIsDeleting(true);
    try {
      // eslint-disable-next-line no-underscore-dangle
      await onDelete(puzzle._id);
    } finally {
      setIsDeleting(false);
    }
  };

  const gridSize = `${puzzle.config?.rows || 3}x${puzzle.config?.columns || 3}`;
  const isComplete = puzzle.state?.isComplete;
  const previewImage = puzzle.state?.previewImage;
  const totalPlayTime = puzzle.stats?.totalPlayTime || 0;
  const moveCount = puzzle.stats?.moveCount || 0;

  return (
    <div className="bg-puzzle-surface rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
      <div className="aspect-video bg-puzzle-muted relative">
        {previewImage ? (
          <img
            src={previewImage}
            alt={puzzle.title || 'Puzzle preview'}
            className="w-full h-full object-contain bg-puzzle-background"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-puzzle-text-secondary">
            <div className="text-center">
              <span className="text-4xl">🧩</span>
              <p className="text-sm mt-2">No preview yet</p>
            </div>
          </div>
        )}
        <div className="absolute top-2 right-2">
          <span
            className={`px-2 py-1 text-xs font-medium rounded ${
              isComplete
                ? 'bg-green-100 text-green-800'
                : 'bg-yellow-100 text-yellow-800'
            }`}
          >
            {isComplete ? 'Complete' : 'In Progress'}
          </span>
        </div>
      </div>

      <div className="p-4">
        <h3 className="font-semibold text-puzzle-text truncate">
          {puzzle.title || 'Untitled Puzzle'}
        </h3>
        <p className="text-sm text-puzzle-text-secondary mt-1">
          {gridSize}
          {' '}
          grid
        </p>

        {/* Stats: Time and Moves */}
        <div className="mt-2 flex items-center gap-4 text-sm text-puzzle-text-secondary">
          <div className="flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{formatTimeHuman(totalPlayTime)}</span>
          </div>
          <div className="flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" />
            </svg>
            <span>
              {moveCount}
              {' '}
              moves
            </span>
          </div>
        </div>

        <div className="mt-3 flex gap-2">
          <Link
            // eslint-disable-next-line no-underscore-dangle
            to={`/play/${puzzle._id}`}
            className="flex-1 text-center px-4 py-2 bg-puzzle-blue text-white text-sm font-medium rounded hover:opacity-90 transition-colors"
          >
            {isComplete ? 'View' : 'Resume'}
          </Link>
          <button
            type="button"
            onClick={handleDelete}
            disabled={isDeleting}
            className="px-4 py-2 bg-red-100 text-red-700 text-sm font-medium rounded hover:bg-red-200 transition-colors disabled:opacity-50"
          >
            {isDeleting ? '...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

PuzzleCard.propTypes = {
  puzzle: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    title: PropTypes.string,
    imageData: PropTypes.string,
    config: PropTypes.shape({
      rows: PropTypes.number,
      columns: PropTypes.number,
    }),
    state: PropTypes.shape({
      isComplete: PropTypes.bool,
      previewImage: PropTypes.string,
    }),
    stats: PropTypes.shape({
      totalPlayTime: PropTypes.number,
      moveCount: PropTypes.number,
    }),
  }).isRequired,
  onDelete: PropTypes.func.isRequired,
};

function MyPuzzles() {
  const { user, token, loading: authLoading } = useContext(AuthContext);
  const navigate = useNavigate();
  const [puzzles, setPuzzles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPuzzles = useCallback(async () => {
    try {
      setError(null);
      const data = await getUserPuzzles();
      setPuzzles(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }

    // Only fetch puzzles when we have both user and token
    if (user && token) {
      fetchPuzzles();
    }
  }, [user, token, authLoading, navigate, fetchPuzzles]);

  const handleDelete = async (puzzleId) => {
    try {
      await deletePuzzle(puzzleId);
      // eslint-disable-next-line no-underscore-dangle
      setPuzzles(puzzles.filter((p) => p._id !== puzzleId));
    } catch (err) {
      setError(err.message);
    }
  };

  if (authLoading || isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-puzzle-danger mb-4">{error}</p>
          <button
            type="button"
            onClick={fetchPuzzles}
            className="px-4 py-2 bg-puzzle-blue text-white rounded hover:opacity-90"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-puzzle-background py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-puzzle-text">My Puzzles</h1>
          <Link
            to="/create"
            className="px-4 py-2 bg-puzzle-blue text-white font-medium rounded hover:opacity-90 transition-colors"
          >
            Create New Puzzle
          </Link>
        </div>

        {puzzles.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-puzzle-text-secondary text-6xl mb-4">🧩</div>
            <h2 className="text-xl font-medium text-puzzle-text mb-2">No puzzles yet</h2>
            <p className="text-puzzle-text-secondary mb-6">Create your first puzzle to get started!</p>
            <Link
              to="/create"
              className="inline-block px-6 py-3 bg-puzzle-blue text-white font-medium rounded-lg hover:opacity-90 transition-colors"
            >
              Create a Puzzle
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {puzzles.map((puzzle) => (
              <PuzzleCard
                // eslint-disable-next-line no-underscore-dangle
                key={puzzle._id}
                puzzle={puzzle}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default MyPuzzles;
